import React, { useCallback, useEffect, useRef, useState } from "react";
import Label from "../../common/Label";
import IconDropdown from "../icons/IconDropdown";
import IconGoogleMeeting from "../icons/IconGoogleMeeting";
import IconMicrosftTeams from "../icons/IconMicrosoftTeams";
import IconScreenMeeting from "../icons/IconScreenMeeting";
import IconSkypeMeeting from "../icons/IconSkypeMeeting";
import IconZoomMeeting from "../icons/IconZoomMeeting";
import {
  Button,
  ButtonArrowIcon,
  ButtonContainer,
  ButtonIcon,
  Cell,
  Container,
  Grid,
  Menu,
  MenuItem,
  Start
} from "./styled/live-meeting-services";

const services = {
  googleMeet: {
    name: "Hangouts",
    url: "http://hangouts.google.com/start",
    icon: <IconGoogleMeeting />
  },
  screen: {
    name: "Screen",
    url: "https://screen.so/#/start",
    icon: <IconScreenMeeting />
  },
  skype: {
    name: "Skype",
    url: "https://www.skype.com/en/free-conference-call/",
    icon: <IconSkypeMeeting />
  },
  teams: {
    name: "Teams",
    url: "https://www.microsoft.com/en-us/microsoft-365/microsoft-teams",
    icon: <IconMicrosftTeams />
  },
  zoom: {
    name: "Zoom",
    url: "https://zoom.us/start/sharemeeting",
    icon: <IconZoomMeeting />
  }
};

function LiveMeetingServices() {
  const menuRef = useRef();
  const [showMenu, setShowMenu] = useState(false);
  const [highlightedService, setHighlightedService] = useState("zoom");
  const [service, setService] = useState("zoom");
  const selectedService = services[service];

  const handleClose = useCallback(
    (newService) => {
      setHighlightedService(newService || service);
      setShowMenu(false);
    },
    [service]
  );

  const handleSelect = (e, service) => {
    e.stopPropagation();
    setService(service);
    handleClose(service);
  };

  const handleKeyDown = (e) => {
    const serviceKeys = Object.keys(services);
    const currentServiceIndex = serviceKeys.findIndex(
      (serviceKey) => serviceKey === highlightedService
    );

    if (["ArrowUp", "ArrowDown", "Home", "End", "Escape"].includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === "ArrowUp") {
      if (!menuRef.current) {
        setShowMenu(true);
        return;
      }
      let nextServiceKey = serviceKeys[currentServiceIndex - 1];
      if (!nextServiceKey) nextServiceKey = serviceKeys[serviceKeys.length - 1];
      setHighlightedService(nextServiceKey);
    } else if (e.key === "ArrowDown") {
      let nextServiceKey = serviceKeys[currentServiceIndex + 1];
      if (!nextServiceKey) nextServiceKey = serviceKeys[0];
      setHighlightedService(nextServiceKey);
    } else if (e.key === "Home") {
      setHighlightedService(serviceKeys[0]);
    } else if (e.key === "End") {
      setHighlightedService(serviceKeys[serviceKeys.length - 1]);
    } else if (e.key === "Enter") {
      if (menuRef.current) {
        e.preventDefault();
      }
      handleSelect(e, highlightedService);
    } else if (e.key === "Escape") {
      e.stopPropagation();
      handleClose();
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        !menuRef.current ||
        e.target === menuRef.current ||
        menuRef.current.contains(e.target)
      ) {
        return;
      }
      handleClose();
    };
    window.addEventListener("click", handler);
    return () => {
      window.removeEventListener("click", handler);
    };
  }, [handleClose]);

  return (
    <>
      <Container>
        <Label htmlFor="services">Start a virtual meeting</Label>
        <Grid>
          <Cell>
            <ButtonContainer>
              <Button
                id="services"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(true);
                }}
                onKeyDown={handleKeyDown}>
                <span>
                  <ButtonIcon>{selectedService.icon}</ButtonIcon>
                  {selectedService.name}
                </span>
                <ButtonArrowIcon>
                  <IconDropdown />
                </ButtonArrowIcon>
              </Button>
              {showMenu && (
                <Menu ref={menuRef}>
                  {Object.entries(services).map(([serviceKey, details]) => (
                    <MenuItem
                      key={serviceKey}
                      isHighlighted={highlightedService === serviceKey}
                      onClick={(e) => handleSelect(e, serviceKey)}
                      onMouseEnter={() => setHighlightedService(serviceKey)}>
                      {details.icon}
                      {details.name}
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </ButtonContainer>
          </Cell>
          <Cell>
            <Start
              className="meeting-services-start"
              href={selectedService.url}
              target="_blank"
              rel="noopener noreferrer">
              Start
            </Start>
          </Cell>
        </Grid>
      </Container>
    </>
  );
}

export default LiveMeetingServices;
